/**
 * Tab Documentos - Página principal com KPIs, filtros e gráficos de documentos
 * Replica a funcionalidade do backup dashboard#documentos
 */
import { 
  DocumentosFilters, 
  DocumentosStatusChart, 
  UnidadesSituacaoCharts,
  UnidadesTabela,
} from './components';
import {
  useDocumentosKpis,
  useDocumentosByStatus,
  useUnitsBySituation,
  useUnitsTable,
} from './hooks';
import { FileText, CheckCircle, Clock, AlertTriangle, Shield } from 'lucide-react';
import { cn } from '@/lib/utils';

// KPI Card para Documentos
interface DocumentosKpiCardProps {
  title: string;
  total: number;
  percentage?: number;
  icon: React.ReactNode;
  isLoading?: boolean;
  className?: string;
  color?: 'default' | 'green' | 'blue' | 'amber' | 'red';
}

function DocumentosKpiCard({
  title,
  total,
  percentage,
  icon,
  isLoading,
  className,
  color = 'default',
}: DocumentosKpiCardProps) {
  const colorClasses = {
    default: '',
    green: 'border-emerald-500/30 bg-emerald-50/50 dark:bg-emerald-950/20',
    blue: 'border-blue-500/30 bg-blue-50/50 dark:bg-blue-950/20',
    amber: 'border-amber-500/30 bg-amber-50/50 dark:bg-amber-950/20',
    red: 'border-red-500/30 bg-red-50/50 dark:bg-red-950/20',
  };

  const textColorClasses = {
    default: 'text-foreground',
    green: 'text-emerald-600 dark:text-emerald-400',
    blue: 'text-blue-600 dark:text-blue-400',
    amber: 'text-amber-600 dark:text-amber-400',
    red: 'text-red-600 dark:text-red-400',
  };

  if (isLoading) {
    return (
      <div className={cn('bg-card rounded-lg border p-4 animate-pulse', className)}>
        <div className="h-4 bg-muted rounded w-24 mb-2" />
        <div className="h-8 bg-muted rounded w-16" />
      </div>
    );
  }

  return (
    <div
      className={cn(
        'bg-card rounded-lg border p-4 transition-all hover:shadow-md flex flex-col',
        colorClasses[color],
        className
      )}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-muted-foreground">{title}</span>
        <span className={cn('', textColorClasses[color])}>{icon}</span>
      </div>
      <div className={cn('text-3xl font-bold', textColorClasses[color])}>
        {total.toLocaleString('pt-BR')}
      </div>
      {percentage !== undefined && (
        <div className="text-xs text-muted-foreground mt-1">
          {percentage.toFixed(1)}% do total
        </div>
      )}
    </div>
  );
}

export function DocumentosTab() {
  // Hooks de dados
  const { data: kpis, isLoading: loadingKpis } = useDocumentosKpis();
  const { data: byStatus, isLoading: loadingByStatus } = useDocumentosByStatus();
  const { data: bySituation, isLoading: loadingSituation } = useUnitsBySituation();
  const { data: unitsTable, isLoading: loadingTable } = useUnitsTable();

  // Calcular percentuais
  const total = kpis?.total || 0;
  const vigentesPercent = total > 0 ? ((kpis?.vigentes || 0) / total) * 100 : 0;
  const permanentesPercent = total > 0 ? ((kpis?.permanentes || 0) / total) * 100 : 0;
  const emAndamentoPercent = total > 0 ? ((kpis?.emAndamento || 0) / total) * 100 : 0;
  const irregularesPercent = total > 0 ? ((kpis?.irregulares || 0) / total) * 100 : 0;

  return (
    <div className="flex flex-col gap-3 md:gap-4 p-0">
      {/* Filtros */}
      <DocumentosFilters />

      {/* Grid principal - 18 colunas */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-18 gap-3 md:gap-4">
        {/* Linha 1: KPIs */}
        <div className="col-span-1 sm:col-span-1 lg:col-span-4 xl:col-span-3">
          <DocumentosKpiCard
            title="Total Documentos"
            total={kpis?.total || 0}
            icon={<FileText className="w-5 h-5" />}
            isLoading={loadingKpis}
            className="h-full"
          />
        </div>
        <div className="col-span-1 sm:col-span-1 lg:col-span-4 xl:col-span-4">
          <DocumentosKpiCard
            title="Permanentes"
            total={kpis?.permanentes || 0}
            percentage={permanentesPercent}
            icon={<Shield className="w-5 h-5" />}
            isLoading={loadingKpis}
            className="h-full"
            color="blue"
          />
        </div>
        <div className="col-span-1 sm:col-span-1 lg:col-span-3 xl:col-span-4">
          <DocumentosKpiCard
            title="Vigentes"
            total={kpis?.vigentes || 0}
            percentage={vigentesPercent}
            icon={<CheckCircle className="w-5 h-5" />}
            isLoading={loadingKpis}
            className="h-full"
            color="green"
          />
        </div>
        <div className="col-span-1 sm:col-span-1 lg:col-span-3 xl:col-span-4">
          <DocumentosKpiCard
            title="Em Andamento"
            total={kpis?.emAndamento || 0}
            percentage={emAndamentoPercent}
            icon={<Clock className="w-5 h-5" />}
            isLoading={loadingKpis}
            className="h-full"
            color="amber"
          />
        </div>
        <div className="col-span-1 sm:col-span-2 lg:col-span-4 xl:col-span-3">
          <DocumentosKpiCard
            title="Irregulares"
            total={kpis?.irregulares || 0}
            percentage={irregularesPercent}
            icon={<AlertTriangle className="w-5 h-5" />}
            isLoading={loadingKpis}
            className="h-full"
            color="red"
          />
        </div>

        {/* Linha 2: Gráficos - lado a lado */}
        <div className="col-span-1 sm:col-span-2 lg:col-span-9">
          <DocumentosStatusChart
            data={byStatus || []}
            isLoading={loadingByStatus}
            totalDocuments={kpis?.total || 0}
          />
        </div>
        <div className="col-span-1 sm:col-span-2 lg:col-span-9">
          <UnidadesSituacaoCharts
            data={bySituation}
            isLoading={loadingSituation}
          />
        </div>

        {/* Linha 3: Tabela de unidades */}
        <div className="col-span-1 sm:col-span-2 lg:col-span-18">
          <UnidadesTabela
            data={unitsTable}
            isLoading={loadingTable}
          />
        </div>
      </div>
    </div>
  );
}
